<div class="container">
  <h1 class="section-title">
    {{ formTitle }}
  </h1>

  <div class="loading" *ngIf="loading">Loading license batch...</div>

  <form
    *ngIf="!loading"
    [formGroup]="form"
    [noValidate]="true"
    (ngSubmit)="submit()"
  >
    <h2 class="section-sub-title">
      {{ devicesOnlyMode ? 'Link Or Unlink Devices' : 'Batch Details' }}
    </h2>

    <div class="form-column half-width">
      <div class="batch-summary" *ngIf="devicesOnlyMode">
        <div class="summary-row">
          <span class="summary-label">Batch ID</span>
          <span class="summary-value">{{ loadedBatch?.id || '-' }}</span>
        </div>
        <div class="summary-row">
          <span class="summary-label">Batch Name</span>
          <span class="summary-value">{{ loadedBatch?.name || '-' }}</span>
        </div>
        <div class="summary-row">
          <span class="summary-label">Product</span>
          <span class="summary-value">{{
            loadedBatch?.product?.name || '-'
          }}</span>
        </div>
        <div class="summary-row">
          <span class="summary-label">Company</span>
          <span class="summary-value">{{
            loadedBatch?.company?.name || loadedBatch?.companyId || '-'
          }}</span>
        </div>
        <div class="summary-row">
          <span class="summary-label">Description</span>
          <span class="summary-value">{{
            loadedBatch?.description || '-'
          }}</span>
        </div>
      </div>

      <ng-container *ngIf="!devicesOnlyMode">
        <div class="full-width">
          <app-autocomplete
            [data]="companies"
            label="Company"
            [isRequired]="!isEditMode"
            [preSelectedValue]="companyPreSelectedValue"
            propertyName="name"
            [disabled]="isEditMode"
            [formatterFn]="companyFormatter"
            (valueSelected)="onCompanySelected($event)"
          ></app-autocomplete>
          <span
            class="inline-error"
            *ngIf="
              companyIdControl.hasError('required') && companyIdControl.touched
            "
          >
            Company is required.
          </span>
        </div>
        <mat-form-field appearance="outline">
          <mat-label>Batch Name</mat-label>
          <input
            class="input-filed"
            type="text"
            matInput
            formControlName="name"
            placeholder="Enter batch name"
          />
          <mat-error *ngIf="nameControl.hasError('required')">
            Batch name is required.
          </mat-error>
          <mat-error *ngIf="nameControl.hasError('pattern')">
            Batch name cannot be blank.
          </mat-error>
          <mat-error *ngIf="nameControl.hasError('maxlength')">
            Batch name must be 160 characters or less.
          </mat-error>
        </mat-form-field>

        <div class="full-width">
          <app-autocomplete
            [data]="products"
            label="Licensed Product"
            [isRequired]="true"
            [preSelectedValue]="productPreSelectedValue"
            propertyName="name"
            [formatterFn]="productFormatter"
            (valueSelected)="onProductSelected($event)"
          ></app-autocomplete>
          <span
            class="inline-error"
            *ngIf="
              licensedProductIdControl.hasError('required') &&
              licensedProductIdControl.touched
            "
          >
            Licensed product is required.
          </span>
        </div>

        <div class="full-width">
          <app-autocomplete
            [data]="scopes"
            label="Scope"
            [isRequired]="true"
            [preSelectedValue]="scopePreSelectedValue"
            propertyName="name"
            [formatterFn]="scopeFormatter"
            (valueSelected)="onScopeSelected($event)"
          ></app-autocomplete>
          <span
            class="inline-error"
            *ngIf="
              scopeIdControl.hasError('required') && scopeIdControl.touched
            "
          >
            Scope is required.
          </span>
        </div>

        <mat-form-field appearance="outline">
          <mat-label>Batch Size</mat-label>
          <input
            class="input-filed"
            type="number"
            min="1"
            matInput
            formControlName="licenseBatchSize"
            placeholder="Enter number of licenses"
          />
          <mat-error *ngIf="licenseBatchSizeControl.hasError('required')">
            Batch size is required.
          </mat-error>
          <mat-error *ngIf="licenseBatchSizeControl.hasError('min')">
            Batch size must be at least 1.
          </mat-error>
        </mat-form-field>
        <mat-form-field
          appearance="outline"
          subscriptSizing="dynamic"
          *ngIf="supportsAutoAssignment"
        >
          <mat-label>Auto-Assign Device Type (optional)</mat-label>
          <mat-select formControlName="autoAssignDeviceWithHardwareComponent">
            <mat-option [value]="''">None (assign devices manually)</mat-option>
            <mat-option
              *ngFor="let deviceType of autoAssignDeviceTypes"
              [value]="deviceType"
            >
              {{ deviceType }}
            </mat-option>
          </mat-select>
          <mat-hint>
            Every device of this type in the company is licensed from this batch
            automatically, while it has spare licenses.
          </mat-hint>
        </mat-form-field>

        <div
          class="device-list"
          *ngIf="
            supportsAutoAssignment &&
            autoAssignDeviceWithHardwareComponentControl.value
          "
        >
          <div class="device-list-title" *ngIf="autoAssignPreviewLoading">
            Checking which devices would be licensed...
          </div>
          <div
            class="auto-assign-preview-warning"
            *ngIf="!autoAssignPreviewLoading && autoAssignPreviewFailed"
          >
            Could not check which devices would be licensed. The list below is
            unavailable until the check succeeds.
          </div>
          <ng-container
            *ngIf="!autoAssignPreviewLoading && !autoAssignPreviewFailed"
          >
            <div
              class="device-list-title"
              *ngIf="autoAssignPreviewDevices.length > 0"
            >
              {{ autoAssignPreviewDevices.length }} device(s) will be licensed
              on save:
            </div>
            <div
              class="device-list-title"
              *ngIf="autoAssignPreviewDevices.length === 0"
            >
              No further devices will be licensed on save.
            </div>
            <a
              class="device-list-item"
              *ngFor="let device of autoAssignPreviewDevices"
              [routerLink]="['/device', device.id]"
              target="_blank"
              rel="noopener"
            >
              {{ device.label }}
            </a>
            <div
              class="auto-assign-preview-warning"
              *ngIf="autoAssignPreviewUnlicensedCount > 0"
            >
              {{ autoAssignPreviewUnlicensedCount }} further matching device(s)
              will not be licensed: this batch has no licenses left for them.
              Increase the batch size to cover them.
            </div>
          </ng-container>
        </div>

        <mat-form-field appearance="outline">
          <mat-label>Description (optional)</mat-label>
          <textarea
            class="input-filed"
            matInput
            rows="3"
            formControlName="description"
            placeholder="Enter batch description"
          ></textarea>
          <mat-error *ngIf="descriptionControl.hasError('maxlength')">
            Description must be 500 characters or less.
          </mat-error>
        </mat-form-field>

        <mat-form-field appearance="outline">
          <mat-label>Valid From (optional)</mat-label>
          <input
            class="input-filed"
            matInput
            formControlName="optionalValidAfter"
            [matDatepicker]="validFromPicker"
          />
          <mat-datepicker-toggle
            matIconSuffix
            [for]="validFromPicker"
          ></mat-datepicker-toggle>
          <mat-datepicker #validFromPicker></mat-datepicker>
        </mat-form-field>

        <mat-form-field appearance="outline">
          <mat-label>Valid To (optional)</mat-label>
          <input
            class="input-filed"
            matInput
            formControlName="optionalValidBefore"
            [matDatepicker]="validToPicker"
          />
          <mat-datepicker-toggle
            matIconSuffix
            [for]="validToPicker"
          ></mat-datepicker-toggle>
          <mat-datepicker #validToPicker></mat-datepicker>
        </mat-form-field>

        <div class="device-list" *ngIf="alreadyLicensedDevices.length > 0">
          <div class="device-list-title">
            {{ alreadyLicensedDevices.length }} device(s) already licensed from
            this batch:
          </div>
          <a
            class="device-list-item"
            *ngFor="let device of alreadyLicensedDevices"
            [routerLink]="['/device', device.id]"
            target="_blank"
            rel="noopener"
          >
            {{ device.label }}
          </a>
        </div>
      </ng-container>

      <ng-container *ngIf="devicesOnlyMode">
        <div class="device-add-row">
          <mat-form-field appearance="outline" class="device-select-field">
            <mat-label>Licensed Device (optional)</mat-label>
            <mat-select formControlName="pendingLicensedDeviceId">
              <mat-option
                *ngFor="let device of selectableDevices"
                [value]="device.deviceId"
              >
                {{ formatDeviceLabel(device) }}
              </mat-option>
            </mat-select>
            <mat-hint *ngIf="!hasCompanySelection">
              Select a company to add devices.
            </mat-hint>
            <mat-hint *ngIf="hasCompanySelection && loadingDevices">
              Loading company devices...
            </mat-hint>
            <mat-hint
              *ngIf="
                hasCompanySelection &&
                !loadingDevices &&
                availableDevices.length === 0
              "
            >
              No devices found for selected company.
            </mat-hint>
            <mat-hint
              *ngIf="
                hasCompanySelection &&
                !loadingDevices &&
                availableDevices.length > 0 &&
                selectableDevices.length === 0
              "
            >
              All company devices are already added.
            </mat-hint>
          </mat-form-field>

          <button
            class="add-device-btn"
            type="button"
            (click)="addSelectedDevice()"
            [disabled]="!canAddSelectedDevice || loadingDevices"
            aria-label="Add selected device"
          >
            <mat-icon class="outlined-icon">add</mat-icon>
          </button>
        </div>

        <div class="inline-error" *ngIf="deviceLoadError">
          {{ deviceLoadError }}
        </div>

        <div
          class="selected-device-list"
          *ngIf="selectedLicensedDevices.length > 0"
        >
          <div
            class="selected-device-item"
            *ngFor="let selected of selectedLicensedDevices"
          >
            <span class="selected-device-label">{{ selected.label }}</span>
            <button
              class="icon-action-btn delete-btn"
              type="button"
              (click)="removeLicensedDevice(selected.id)"
              [attr.aria-label]="'Remove ' + selected.label"
            >
              <mat-icon class="outlined-icon">delete</mat-icon>
            </button>
          </div>
        </div>
      </ng-container>
    </div>

    <app-response-banner
      [success]="successfulSave"
      [successMessage]="successMessage"
      [errorMessage]="errorMessage"
      [invisible]="!showResponseBanner"
    ></app-response-banner>

    <div class="button-box half-width">
      <app-button [title]="'Cancel'" (click)="cancel()"></app-button>
      <app-button
        [tall]="true"
        [title]="submitButtonLabel"
        [disabled]="(devicesOnlyMode ? false : form.invalid) || saving"
      ></app-button>
    </div>
  </form>
</div>
